import { useAutoneTranslation } from '@autone/translations';
import { cva, type VariantProps } from 'class-variance-authority';
import { AnimatePresence, motion } from 'motion/react';
import {
  forwardRef,
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Button, type ButtonProps, Tooltip } from '../../../atoms';
import { CheckIcon, CopyIcon } from '../../../icons';
import { type AutoneIconComponent } from '../../../icons/factory/create-icon-component';
import { cn } from '../../../utils';

const copyButtonVariants = cva('', {
  variants: {
    variant: {
      default: 'copyText-primary-foreground',
      secondary: 'copyText-secondary-foreground',
      ghost: 'copyText-secondary-foreground',
    },
  },
});
export interface CopyButtonProps
  extends Omit<ButtonProps, 'size' | 'children' | 'onCopy' | 'variant' | 'ref'>,
    VariantProps<typeof copyButtonVariants> {
  copyText: string;
  size?: 'default' | 'sm' | 'xs';
  tooltipText?: string;
  delay?: number;
  onCopy?: (content: string) => void;
  icon?: AutoneIconComponent;
}

export type CopyButtonSpecialisedProps = Omit<
  CopyButtonProps,
  'icon' | 'tooltipText' | 'copyText'
>;

const sizeMap = {
  default: 'icon',
  sm: 'icon-sm',
  xs: 'icon-xs',
} as const;

const iconSizeClassMap = {
  default: 'size-6',
  sm: 'size-[18px]',
  xs: 'size-[14px]',
} as const;

export const CopyButton = forwardRef<HTMLButtonElement, CopyButtonProps>(
  (
    {
      icon = CopyIcon,
      size = 'default',
      copyText,
      tooltipText,
      variant,
      onClick,
      delay = 3000,
      onCopy,
      ...props
    },
    ref,
  ) => {
    const { common } = useAutoneTranslation();
    const tooltipTextValue = tooltipText ?? common('copy-to-clipboard');
    const firstMount = useRef(true);

    useEffect(() => {
      firstMount.current = false;
      return () => {
        firstMount.current = true;
      };
    }, []);

    const [isCopied, setIsCopied] = useState(false);
    const Icon = isCopied ? CheckIcon : icon;

    const handleCopy = useCallback(
      (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        navigator.clipboard.writeText(copyText).then(() => {
          setIsCopied(true);
          setTimeout(() => setIsCopied(false), delay);
          onCopy?.(copyText);
        });
        onClick?.(e);
      },
      [copyText, delay, onCopy, onClick],
    );

    return (
      <Tooltip content={tooltipTextValue} asChild>
        <Button
          ref={ref}
          variant={variant}
          {...props}
          size={sizeMap[size]}
          onClick={handleCopy}
        >
          <AnimatePresence mode="wait">
            <motion.span
              key={isCopied ? 'check' : 'copy'}
              data-slot="copy-button-icon"
              initial={firstMount.current ? { scale: 1 } : { scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{
                type: 'spring',
                stiffness: 600,
                mass: 0.4,
                damping: 30,
              }}
            >
              <Icon
                className={cn(
                  iconSizeClassMap[size],
                  copyButtonVariants({
                    variant,
                  }),
                )}
              />
            </motion.span>
          </AnimatePresence>
        </Button>
      </Tooltip>
    );
  },
);
